import axios, { AxiosRequestConfig } from 'axios';

import { API_URL } from 'src/config-global';
import { AxiosHttpClient } from 'src/http/axios.http';

const axiosInstance = axios.create({
  baseURL: API_URL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers['Content-Type'] = 'application/json';

    return { ...config };
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

export const api = new AxiosHttpClient(API_URL);

export const endpoints = {
  auth: {
    login: 'auth/login',
    userData: 'auth/user-data',
    newToken: 'auth/new-token',
    termsCheck: 'auth/terms/check',
    termsAccept: 'auth/terms/accept',
    deleteAccount: 'auth/',
    paymentErrorCount: 'auth/payment-authentication/error-count',
    paymentFailure: 'auth/payment-authentication/failure',
    deeplink: 'auth/deeplink',
    updatePhoto: 'auth/upload-profile-image',
    updateUser: 'auth/update-user-data',
    myMemberCard: 'auth/my-member-card',
    confirmOnboarding: 'auth/confirm-onboarding',
    getConfig: 'auth/config/by-name',
    getPromotionOption: 'benefits/promotion/check-promotion',
  },
  home: {
    missions: 'home/missions',
    newMissions: 'benefits/missions/home-missions',
    events: 'benefits/events/home-events',
    highlights: 'benefits/home-highlights',
    benefits: 'benefits/coupons/home-coupons',
    stores: 'benefits/store/home-stores',
    otherBenefits: 'benefits/exclusive-benefits',
    banners: 'auth/home-banner-page/user-banner',
    flashOffers: 'partners/crmads/offers',
    homeBand: 'auth/home-band/user-band',
    benefitsThatAwaitsYou: 'benefits/product/awaiting-benefits',
    news: 'benefits/product/products-news',
    allBenefits: 'benefits/all-benefits-for-user',
  },
  wallet: {
    myBalance: 'wallet/my-balance',
    userPointsNotifications: 'wallet/user-points-notifications',
  },
  benefits: {
    suggestion: {
      sendSuggestion: 'benefits/send-suggestion',
    },
    experience: {
      list: 'benefits/experience/list',
      highlight: 'benefits/experience/highlights',
      details: (id: string) => `benefits/experience/details/${id}`,
      redeem: (id: string) => `benefits/experience/redeem/${id}`,
    },
    product: {
      list: 'benefits/product/by-category',
      highlight: 'benefits/product/highlights',
      details: (id: string) => `benefits/product/details/${id}`,
      rescueDetails: (id: string, redemptionId: string) =>
        `benefits/product/redeemed-item-details/${id}/${redemptionId}`,
      rescue: 'benefits/product/redeem',
      allFilter: 'benefits/product/filter',
      category: 'benefits/product-category',
      byTheme: (themeId: string) => `benefits/product/by-theme/${themeId}`,
      recomendation: 'benefits/product/products-recomendations',
    },
    userFiltered: 'benefits/user-filtered',
    userFilteredIsNew: 'benefits/user-filtered-is-new',
    coupons: {
      couponsList: 'benefits/coupons/coupons-page',
      couponsWithFilter: 'benefits/coupons/filter',
      highlightCoupons: 'benefits/coupons/highlight',
      benefitsCoupons: 'benefits/coupons/benefits',
      discounts: 'benefits/coupons/discounts',
      coupomDetails: (id: string) => `benefits/coupons/details/${id}`,
      discountDetails: (id: string) => `benefits/coupons/discounts-details/${id}`,
      rescueCoupon: 'benefits/coupons/redeem',
      findInternal: 'partners/external/find-internal',
      useCoupon: 'benefits/coupons/use-coupon',
      rescueBenefit: 'benefits/coupons/benefitRescue',
      couponRescueDetails: (id: string) => `benefits/coupons/redemption-details/${id}`,
      benefitRescueDetails: (id: string) => `benefits/coupon/benefits-rescue-details/${id}`,
      crmAdsHighlights: 'partners/crmads/offers',
      crmAdsDetails: (id: string) => `partners/crmads/offers/details?offerId=${id}`,
      purchaseCrmAd: 'partners/crmads/offers/purchase',
      offerRescueDetails: (id: string) =>
        `partners/crmads/offers/purchased-details?purchasedOfferId=${id}`,
    },
    events: {
      list: 'benefits/events/list',
      lastDays: 'benefits/events/expiring',
      myFavorites: 'benefits/events/my-favorites',
      mySchedules: 'benefits/events/my-schedules',
      myExpired: 'benefits/events/my-expired',
      details: `benefits/events/details`,
      scheduleDetailsNew: `benefits/events/schedule-details`,
      scheduleDetails: `benefits/events/available-times`,
      favorite: `benefits/events/favorite`,
      cancelSchedule: `benefits/events/cancel`,
      schedule: `benefits/events/redeem`,
    },
    promotion: {
      list: 'benefits/promotion/list',
      highlight: 'benefits/promotion/highlights',
      my: 'benefits/promotion/my-participations',
      details: (id: string) => `benefits/promotion/details/${id}`,
      participate: `benefits/promotion/participate`,
    },
    collectibles: {
      list: 'benefits/collections/active-collection-details',
      collectionTimer: 'benefits/collectibles/collection-timer',
      unlockItem: 'benefits/collectibles/unlock-item',
      collectibleDetails: 'benefits/collectibles/details',
      rescueCollectible: 'benefits/collectibles/redeem',
      levelPoints: 'benefits/collectibles/level-points',
      newUnlockedItems: 'benefits/collectibles/show-unlocked-collectibles',
      verifyActiveCollection: 'benefits/collections/verify-active-collection',
      collectibleRedemptionDetails: (collectibleRedemptionId: string) =>
        `benefits/collectibles-redemptions/details/${collectibleRedemptionId}`,
    },
  },
  profile: {
    myExchangesList: 'benefits/my-trades',
  },
  test: {
    generatePoints: 'auth/add-points-test',
    spentPoints: 'auth/remove-points-test',
    getExtract: 'wallet/my-records',
  },
  points: {
    list: 'wallet/points-list',
    details: 'wallet/points-details',
    expiringPoints: 'wallet/expiring-points',
    notesList: 'points/notes-list',
  },
  missions: {
    list: 'missions/missions/user/all',
    details: (id: string) => `missions/missions/user/details?missionId=${id}`,
    customScanDetails: (id: string) => `missions/missions/user/custom-scan-details?missionId=${id}`,
    customScanProgress: (id: string) => `missions/missions/user/execution-details?missionId=${id}`,
    customScanV2Progress: (id: string) =>
      `missions/missions/user/execution-details-v2?missionId=${id}`,
    startQuiz: 'missions/missions/user/start',
    checkIn: 'missions/missions/user/check-in',
    quizQuestions: 'missions/quiz-questions',
    finishQuiz: 'missions/missions/user/finish-quiz',
    quizResult: 'missions/quiz-result',
    registerCarPlate: 'missions/missions/user/register-car-plate',
    carPlateResult: 'missions/car-plate/result',
    finishInstant: 'missions/missions/user/instant',
  },
  invoices: {
    myInvoices: 'invoices/my-invoices',
  },
  search: {
    results: 'benefits/search',
    recently: 'benefits/my-recent-searches',
    popular: 'benefits/popular-searches',
    searchItemSelected: 'benefits/search-item-selected',
  },
  help: {
    frequentQuestions: (area: string) => `auth/faq/area-filtered?area=${area}`,
    levelsQuestions: 'help/levels-questions',
    pointsQuestions: 'help/points-questions',
    notesQuestions: 'help/notes-questions',
    regulations: 'auth/terms/terms-page',
    regulationDetais: (id: string) => `auth/terms/term?termId=${id}`,
    promotionalAction: (id: string) => `help/promotional-actions/${id}`,
  },
  games: {
    memoryGameDetails: (id: string) => `games/memory-game/details/${id}`,
    memoryGameCards: (id: string) => `games/memory-game/card/${id}`,
    memoryGameFinish: `games/memory-game/finish`,
    memoryGameResults: (id: string) => `games/memory-game/results/${id}`,
  },
  myInterests: {
    interestsList: 'my-interests/interests-list',
    sendMyInterests: 'auth/profile/finish-quiz',
    myInterestsResult: 'auth/profile/user-profile-details',
    reorderPreferences: 'auth/profile/switch-profiles',
    quizQuestions: 'auth/profile/quizQuestions',
    maybeLater: 'auth/profile/maybe-later',
  },
  myLevel: {
    myLevelInfo: 'tiers/user-level',
    nextLevelBenefits: 'benefits/next-tier',
  },
  analytics: {
    eventsMany: 'analytics/events/many',
  },
  appTexts: {
    findTextsByScreen: (screen: string) => `benefits/app-text/screen/${screen}`,
  },
  achievementsTrails: {
    byUser: (id: string) => `achievements/trails/${id}/summary`,
    phaseDetails: (id: string) => `achievements/trails/phases/${id}`,
    showCompletedTrail: (id: string) => `achievements/trails/${id}/show-completed-trail`,
    assign: 'achievements/trails/assign',
    redeem: (id: string) => `achievements/rewards/${id}/redeem`,
  },
  onboarding: 'benefits/content-control/onboarding-details',
};
